import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  Loader2, Check, AlertTriangle, Clock, MailCheck, Plus, Trash2,
  ChevronLeft, ChevronRight, Send, ShieldCheck,
} from 'lucide-react';
import { Button, Checkbox } from '../ui-v2';
import { Input, Select, FileDrop } from '../ui-v2.5';
import { cargarFormularioPublico, guardarFormularioPublico, subirDocumentoPublico } from './api';
import { fmtFecha } from './fecha';
// Mismo mecanismo de CP → provincia/población que el alta de empleado (FormCrearPersona).
import { buscarDireccionPorCP } from '../../../utils/direccionesUtils';

/**
 * FormularioPublico — formulario del candidato (Fase 8). Ruta PÚBLICA sin gate de sesión
 * (/incorporaciones/formulario/:token). Cinco pasos: Personales, Dirección, RRHH, Formación,
 * Documentos. Estados de entrada: válido / caducado (410) / inválido (404) / ya enviado (200 con
 * estado 'recibida' → "ya enviaste tus datos el día X"). Tras enviar, pantalla de confirmación.
 * IBAN y nº SS se envían en claro por HTTPS y el backend los cifra; nunca vuelven descifrados.
 */

const PASOS = ['Personales', 'Dirección', 'RRHH', 'Formación', 'Documentos'];
const ESTADO_CIVIL = ['Soltero/a', 'Casado/a', 'Pareja de hecho', 'Divorciado/a', 'Viudo/a', 'Otro'];
const cuerpoError = (e) => (e && e.response && e.response.data) || {};

// Años para el nacimiento de los hijos: desplegable desde el año EN CURSO hacia abajo (calculado,
// no una constante que envejece). 80 años cubre cualquier hijo declarable sin ser una lista infinita.
const ANIO_ACTUAL = new Date().getFullYear();
const ANIOS_HIJOS = Array.from({ length: 80 }, (_, i) => ANIO_ACTUAL - i);

const FORM_INICIAL = {
  direccion: '', codigo_postal: '', poblacion: '', provincia: '', direccion_teletrabajo: '',
  lugar_nacimiento: '', fecha_nacimiento: '', telefono_personal: '', estado_civil: '',
  iban: '', num_seguridad_social: '',
};


// Envoltorio de página centrada (sin chrome de intranet).
function Pagina({ children }) {
  return (
    <div className="min-h-screen w-full bg-v25-surface-sunken px-4 py-10">
      <div className="mx-auto w-full max-w-2xl">{children}</div>
    </div>
  );
}

function EstadoMensaje({ icon: Icon, tono, titulo, children }) {
  const map = {
    error: 'border-v25-expired-bd bg-v25-expired-bg text-v25-expired-fg',
    ok: 'border-v25-received-bd bg-v25-received-bg text-v25-received-fg',
  };
  return (
    <Pagina>
      <div className={`rounded-v25-lg border ${map[tono] || map.error} p-8 text-center`}>
        <Icon className="mx-auto mb-3 h-10 w-10" />
        <h1 className="mb-2 text-lg font-semibold">{titulo}</h1>
        <div className="text-sm">{children}</div>
      </div>
    </Pagina>
  );
}

export default function FormularioPublico() {
  const { token } = useParams();
  const [fase, setFase] = useState('cargando'); // cargando|valido|caducado|invalido|ya_enviado|enviado
  const [info, setInfo] = useState(null);
  const [paso, setPaso] = useState(1);
  const [form, setForm] = useState(FORM_INICIAL);
  const [conocidos, setConocidos] = useState({ nombre: '', apellidos: '', email: '' });
  const [hijos, setHijos] = useState([]);
  const [titulaciones, setTitulaciones] = useState([]);
  const [docs, setDocs] = useState({ anverso: null, reverso: null }); // { path, name }
  const [subiendo, setSubiendo] = useState({ anverso: false, reverso: false });
  const [consentimiento, setConsentimiento] = useState(false);
  const [enviando, setEnviando] = useState(false);

  useEffect(() => {
    let cancel = false;
    setFase('cargando');
    cargarFormularioPublico(token)
      .then((d) => {
        if (cancel) return;
        // 'recibida' = ya envió sus datos (§4.7; antes el backend decía 'completada' aquí).
        if (d.estado === 'recibida' || d.estado === 'completada') { setInfo(d); setFase('ya_enviado'); return; }
        setInfo(d);
        setConocidos({ nombre: d.candidato?.nombre || '', apellidos: d.candidato?.apellidos || '', email: d.candidato?.email || '' });
        setFase('valido');
      })
      .catch((e) => {
        if (cancel) return;
        const st = e?.response?.status;
        setFase(st === 410 ? 'caducado' : 'invalido'); // 404 y otros → genérico
      });
    return () => { cancel = true; };
  }, [token]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  // Teléfono personal: SOLO dígitos (mismo criterio que el wizard de RRHH).
  const setTelefono = (e) => setForm((f) => ({ ...f, telefono_personal: String(e.target.value || '').replace(/\D/g, '') }));

  // Código postal → provincia y población automáticas, con el mecanismo del alta de empleado.
  // Quedan editables: si el CP tiene varias poblaciones, el candidato corrige la que le toque.
  const setCodigoPostal = (e) => {
    const cp = String(e.target.value || '').replace(/\D/g, '').slice(0, 5);
    setForm((f) => {
      const nf = { ...f, codigo_postal: cp };
      if (cp.length === 5) {
        const dir = buscarDireccionPorCP(cp);
        if (dir) { nf.provincia = dir.provincia; nf.poblacion = dir.poblacion; }
      }
      return nf;
    });
  };

  async function subir(cara, file) {
    if (!file) { setDocs((d) => ({ ...d, [cara]: null })); return; }
    setSubiendo((s) => ({ ...s, [cara]: true }));
    try {
      const res = await subirDocumentoPublico(token, file, cara);
      setDocs((d) => ({ ...d, [cara]: { path: res.path, name: file.name } }));
      toast.success(`DNI (${cara}) subido.`);
    } catch (e) {
      toast.error(cuerpoError(e).message || 'No se pudo subir la imagen.');
      setDocs((d) => ({ ...d, [cara]: null }));
    } finally {
      setSubiendo((s) => ({ ...s, [cara]: false }));
    }
  }

  async function enviar() {
    if (!consentimiento) { toast.error('Debes aceptar el tratamiento de datos antes de enviar.'); return; }
    const documentos = [];
    if (docs.anverso) documentos.push({ cara: 'anverso', path: docs.anverso.path });
    if (docs.reverso) documentos.push({ cara: 'reverso', path: docs.reverso.path });
    const payload = {
      consentimiento: true,
      ...form,
      hijos: hijos.map((h) => ({ anio_nacimiento: Number(h.anio_nacimiento) || null })),
      titulaciones: titulaciones.map((t) => ({
        titulo: t.titulo || null, tipo: t.tipo || null, centro: t.centro || null,
        anio: Number(t.anio) || null,
      })),
      documentos,
    };
    setEnviando(true);
    try {
      await guardarFormularioPublico(token, payload);
      setFase('enviado');
    } catch (e) {
      const st = e?.response?.status;
      if (st === 409) { setInfo({ estado: 'recibida' }); setFase('ya_enviado'); }
      else if (st === 410) setFase('caducado');
      else toast.error(cuerpoError(e).message || 'No se pudo enviar el formulario.');
    } finally {
      setEnviando(false);
    }
  }

  // ── Estados de entrada / salida ──
  if (fase === 'cargando') {
    return <Pagina><div className="flex items-center justify-center gap-2 py-24 text-sm text-v25-ink-500"><Loader2 className="h-5 w-5 animate-spin" /> Cargando…</div></Pagina>;
  }
  if (fase === 'invalido') {
    return <EstadoMensaje icon={AlertTriangle} tono="error" titulo="Enlace no válido">Este enlace no es válido. Comprueba que lo has copiado completo o pide uno nuevo a la empresa.</EstadoMensaje>;
  }
  if (fase === 'caducado') {
    return <EstadoMensaje icon={Clock} tono="error" titulo="Enlace caducado">Este enlace ha caducado. Ponte en contacto con la empresa para que te envíe uno nuevo.</EstadoMensaje>;
  }
  if (fase === 'ya_enviado') {
    const fecha = fmtFecha(info?.enviado_en, '');
    return (
      <EstadoMensaje icon={MailCheck} tono="ok" titulo="Ya enviaste tus datos">
        {info?.candidato?.nombre ? <p className="mb-1">Hola {info.candidato.nombre},</p> : null}
        <p>Tus datos {fecha ? <>se enviaron el <strong>{fecha}</strong></> : 'ya se enviaron'}. No hace falta que hagas nada más.</p>
        {info?.empresa ? <p className="mt-1 text-xs text-v25-ink-500">{info.empresa}</p> : null}
      </EstadoMensaje>
    );
  }
  if (fase === 'enviado') {
    return (
      <EstadoMensaje icon={Check} tono="ok" titulo="¡Datos enviados!">
        <p>Gracias{conocidos.nombre ? `, ${conocidos.nombre}` : ''}. Hemos recibido tus datos correctamente.</p>
        <p className="mt-1 text-xs text-v25-ink-500">Ya puedes cerrar esta página.</p>
      </EstadoMensaje>
    );
  }

  // ── Wizard (fase 'valido') ──
  const ultimo = paso === PASOS.length;
  const caducaTxt = fmtFecha(info?.caduca_en, '');

  return (
    <div className="min-h-screen bg-v25-surface-sunken pb-10">
      {/* Cabecera de MARCA (full-bleed, azul corporativo v25-brand-800 = #003c66) con el indicador de pasos encima */}
      <div className="bg-v25-brand-800 text-white">
        <div className="mx-auto max-w-2xl px-6 py-6">
          <div className="mb-1 flex items-center gap-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-md bg-white/20 text-sm font-bold text-white">GL</div>
            <span className="text-base font-semibold">GRUPO LAE</span>
          </div>
          <h1 className="mt-3 text-xl font-semibold">
            {conocidos.nombre ? <>Bienvenido/a, {conocidos.nombre}</> : 'Bienvenido/a'}
          </h1>
          <p className="mt-1 text-sm text-white/70">
            Completa tus datos para preparar tu incorporación{info?.empresa ? <> a <b className="font-semibold text-white">{info.empresa}</b></> : ''}.
            {caducaTxt ? <> Enlace seguro, caduca el {caducaTxt}.</> : ' Enlace seguro.'}
          </p>

          {/* Indicador de pasos sobre la cabecera (puntos + líneas, como el mockup) */}
          <div className="mt-5 flex items-center gap-2">
            {PASOS.map((_, i) => (
              <React.Fragment key={i}>
                {i > 0 && <div className="h-px flex-1 bg-white/30" />}
                <div className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${i + 1 <= paso ? 'bg-white text-v25-brand-800' : 'bg-white/20 text-white/70'}`}>
                  {i + 1}
                </div>
              </React.Fragment>
            ))}
          </div>
          <div className="mt-1.5 flex justify-between text-[11px] text-white/70">
            {PASOS.map((p) => <span key={p}>{p}</span>)}
          </div>
        </div>
      </div>

      {/* Contenido en tarjeta centrada */}
      <div className="mx-auto max-w-2xl px-6">
        <div className="mt-4 overflow-hidden rounded-v25-lg border border-v25-line bg-white">
          <div className="border-b border-v25-line px-6 py-4">
            <h2 className="text-base font-semibold text-v25-ink-900">Paso {paso} de {PASOS.length}: {PASOS[paso - 1]}</h2>
          </div>

          {/* Contenido del paso */}
          <div className="space-y-5 px-6 py-6">
          {paso === 1 && (
            <>
              {/* Campos ya conocidos, en gris. Apellidos puede venir vacío en solicitudes anteriores
                  a la columna `apellidos_candidato`: entonces no se pinta el hueco. */}
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <Input label="Nombre" value={conocidos.nombre} disabled hint="Ya lo sabemos" />
                {conocidos.apellidos
                  ? <Input label="Apellidos" value={conocidos.apellidos} disabled hint="Ya lo sabemos" />
                  : null}
                <Input label="Email" value={conocidos.email} disabled hint="Ya lo sabemos" />
              </div>
              {/* Lugar de nacimiento NO está aquí: va con la dirección (paso 2, §4.7). */}
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <Input label="Fecha de nacimiento" type="date" value={form.fecha_nacimiento} onChange={set('fecha_nacimiento')} />
                <Input label="Teléfono personal" value={form.telefono_personal} onChange={setTelefono}
                  inputMode="numeric" maxLength={20} hint="Solo dígitos." />
                <Select label="Estado civil" value={form.estado_civil} onChange={set('estado_civil')}>
                  <option value="">Prefiero no indicarlo</option>
                  {ESTADO_CIVIL.map((e) => <option key={e} value={e}>{e}</option>)}
                </Select>
              </div>
              {/* Hijos (dinámico) — el año es un desplegable, no campo libre: evita erratas de
                  tecleo (3 dígitos, año futuro) en un dato que va a `persona_hijos.anio_nacimiento`. */}
              <ListaDinamica
                titulo="Hijos"
                items={hijos}
                onAdd={() => setHijos((h) => [...h, { anio_nacimiento: '' }])}
                onRemove={(i) => setHijos((h) => h.filter((_, idx) => idx !== i))}
                vacio="Sin hijos que declarar."
                render={(h, i) => (
                  <Select label="Año de nacimiento" value={h.anio_nacimiento}
                    onChange={(e) => setHijos((arr) => arr.map((x, idx) => idx === i ? { anio_nacimiento: e.target.value } : x))}>
                    <option value="">Selecciona…</option>
                    {ANIOS_HIJOS.map((a) => <option key={a} value={a}>{a}</option>)}
                  </Select>
                )}
              />
            </>
          )}

          {paso === 2 && (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="sm:col-span-2"><Input label="Dirección" value={form.direccion} onChange={set('direccion')} maxLength={255} /></div>
              <Input label="Código postal" value={form.codigo_postal} onChange={setCodigoPostal}
                inputMode="numeric" maxLength={5} hint="Rellena provincia y población." />
              <Input label="Población" value={form.poblacion} onChange={set('poblacion')} maxLength={120} />
              <Input label="Provincia" value={form.provincia} onChange={set('provincia')} maxLength={120} />
              <Input label="Lugar de nacimiento" value={form.lugar_nacimiento} onChange={set('lugar_nacimiento')} maxLength={120} />
              <div className="sm:col-span-2"><Input label="Dirección de teletrabajo (si distinta)" value={form.direccion_teletrabajo} onChange={set('direccion_teletrabajo')} maxLength={255} /></div>
            </div>
          )}

          {paso === 3 && (
            <>
              <div className="flex items-start gap-2 rounded-v25-md border border-v25-line bg-v25-surface-sunken p-3 text-xs text-v25-ink-500">
                <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-v25-ink-400" />
                Estos datos se guardan cifrados y solo los ve Recursos Humanos.
              </div>
              <Input label="IBAN" value={form.iban} onChange={set('iban')} maxLength={34} placeholder="ESXX XXXX XXXX XXXX XXXX XXXX" />
              <Input label="Nº de la Seguridad Social" value={form.num_seguridad_social} onChange={set('num_seguridad_social')} maxLength={20} />
            </>
          )}

          {paso === 4 && (
            <ListaDinamica
              titulo="Titulaciones"
              items={titulaciones}
              onAdd={() => setTitulaciones((t) => [...t, { titulo: '', tipo: '', centro: '', anio: '' }])}
              onRemove={(i) => setTitulaciones((t) => t.filter((_, idx) => idx !== i))}
              vacio="Sin titulaciones que declarar."
              render={(t, i) => {
                const upd = (k, v) => setTitulaciones((arr) => arr.map((x, idx) => idx === i ? { ...x, [k]: v } : x));
                return (
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                    <Input label="Título" value={t.titulo} onChange={(e) => upd('titulo', e.target.value)} maxLength={200} />
                    <Input label="Tipo (FP, Grado…)" value={t.tipo} onChange={(e) => upd('tipo', e.target.value)} maxLength={60} />
                    <Input label="Centro" value={t.centro} onChange={(e) => upd('centro', e.target.value)} maxLength={200} />
                    <Input label="Año" type="number" value={t.anio} onChange={(e) => upd('anio', e.target.value)} />
                  </div>
                );
              }}
            />
          )}

          {paso === 5 && (
            <>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <CaraDni label="DNI · anverso" cara="anverso" doc={docs.anverso} subiendo={subiendo.anverso} onFile={(f) => subir('anverso', f)} />
                <CaraDni label="DNI · reverso" cara="reverso" doc={docs.reverso} subiendo={subiendo.reverso} onFile={(f) => subir('reverso', f)} />
              </div>
              <label className="flex cursor-pointer items-start gap-2 rounded-v25-md border border-v25-line p-3">
                <Checkbox checked={consentimiento} onChange={(e) => setConsentimiento(e.target.checked)} className="mt-0.5" />
                <span className="text-sm text-v25-ink-700">
                  Consiento el tratamiento de mis datos personales para la gestión de mi incorporación,
                  conforme a la política de privacidad de la empresa. <span className="text-red-600">*</span>
                </span>
              </label>
            </>
          )}
        </div>

        {/* Navegación */}
        <div className="flex items-center justify-between border-t border-v25-line px-6 py-4">
          <Button variant="outline" onClick={() => setPaso((p) => Math.max(1, p - 1))} disabled={paso === 1 || enviando}>
            <ChevronLeft />Atrás
          </Button>
          {ultimo ? (
            <Button onClick={enviar} disabled={enviando || !consentimiento}>
              {enviando ? <Loader2 className="animate-spin" /> : <Send />}Enviar mis datos
            </Button>
          ) : (
            <Button onClick={() => setPaso((p) => Math.min(PASOS.length, p + 1))}>
              Siguiente<ChevronRight />
            </Button>
          )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────── Lista dinámica (hijos / titulaciones) ───────────────────────────
function ListaDinamica({ titulo, items, onAdd, onRemove, render, vacio }) {
  return (
    <section className="space-y-2 border-t border-v25-line pt-4">
      <div className="flex items-center justify-between">
        <h4 className="text-xs font-semibold uppercase tracking-wide text-v25-ink-500">{titulo}</h4>
        <Button variant="outline" size="sm" onClick={onAdd}><Plus />Añadir</Button>
      </div>
      {items.length === 0 ? (
        <p className="text-xs text-v25-ink-400">{vacio}</p>
      ) : (
        <div className="space-y-3">
          {items.map((it, i) => (
            <div key={i} className="flex items-start gap-2 rounded-v25-md border border-v25-line p-3">
              <div className="min-w-0 flex-1">{render(it, i)}</div>
              <button type="button" onClick={() => onRemove(i)} className="mt-6 shrink-0 text-v25-ink-400 hover:text-red-600" aria-label="Quitar"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

// ─────────────────────────── Una cara del DNI ───────────────────────────
function CaraDni({ label, doc, subiendo, onFile }) {
  return (
    <div>
      <label className="mb-1 block text-xs font-medium text-v25-ink-700">{label}</label>
      <FileDrop
        accept="image/png,image/jpeg,image/webp"
        maxSize={5}
        value={doc ? { name: doc.name } : null}
        onChange={onFile}
        hint="JPG, PNG o WebP · máx. 5 MB"
      />
      {subiendo && <p className="mt-1 flex items-center gap-1 text-xs text-v25-ink-500"><Loader2 className="h-3 w-3 animate-spin" /> Subiendo…</p>}
      {doc && !subiendo && <p className="mt-1 flex items-center gap-1 text-xs text-v25-received-fg"><Check className="h-3 w-3" /> Subido</p>}
    </div>
  );
}
